import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface SavedView {
  id: string;
  name: string;
  description?: string;
  page_type: string;
  state_data: Record<string, unknown>;
  created_by: string;
  created_by_email?: string;
  updated_by: string;
  updated_by_email?: string;
  organization_id: string;
  organization_name?: string;
  created_at: string;
  updated_at: string;
  version: number;
}

export interface CreateSavedViewData {
  name: string;
  description?: string;
  page_type: string;
  state_data: Record<string, unknown>;
}

export interface UpdateSavedViewData {
  name?: string;
  description?: string;
  state_data?: Record<string, unknown>;
}

export type SavedViewsResponse = SavedView[] | { results: SavedView[] };

// Hook to fetch all saved views for a page type
export function useSavedViews(pageType: string) {
  return useQuery({
    queryKey: ["saved-views", pageType],
    queryFn: async () => {
      const response = await api.get<SavedViewsResponse>(
        `/saved-views/?page_type=${pageType}`,
      );

      if (Array.isArray(response)) {
        return response;
      }

      if ("results" in response) {
        return response.results || [];
      }

      return [];
    },
    enabled: !!pageType,
  });
}

// Hook to fetch a single saved view
export function useSavedView(viewId: string | null | undefined) {
  return useQuery({
    queryKey: ["saved-view", viewId],
    queryFn: async () => {
      if (!viewId) return null;
      const response = await api.get<SavedViewResponse>(
        `/saved-views/${viewId}/`,
      );
      if ("saved_view" in response) {
        return response.saved_view;
      }

      return response;
    },
    enabled: !!viewId,
  });
}

// Hook to fetch a one-time share or saved view from /s/ endpoint
export function useShare(shareId: string | null | undefined) {
  return useQuery({
    queryKey: ["share", shareId],
    queryFn: async () => {
      if (!shareId) return null;
      const response = await api.get<
        SavedView & {
          share_type?: string;
          is_one_time?: boolean;
          message?: string;
        }
      >(`/s/${shareId}/`);
      return response;
    },
    enabled: !!shareId,
  });
}

export type SavedViewResponse = SavedView | { saved_view: SavedView };

export type UpdateSavedViewResponse = SavedView | { saved_view: SavedView };

// Hook to create a new saved view
export function useCreateSavedView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSavedViewData) => {
      const response = await api.post<SavedViewResponse>("/saved-views/", data);
      if ("saved_view" in response) {
        return response.saved_view;
      }

      return response;
    },
    onSuccess: (newView) => {
      // Invalidate the list query for this page type
      queryClient.invalidateQueries({
        queryKey: ["saved-views", newView.page_type],
      });
    },
    onError: () => {
      // Handle error silently - UI should show appropriate message
    },
  });
}

// Hook to update an existing saved view
export function useUpdateSavedView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      viewId,
      ...data
    }: { viewId: string } & UpdateSavedViewData) => {
      // Only send the data fields, not viewId
      const response = await api.patch<UpdateSavedViewResponse>(
        `/saved-views/${viewId}/`,
        data,
      );
      if ("saved_view" in response) {
        return response.saved_view;
      }

      return response;
    },
    onSuccess: (updatedView) => {
      // Invalidate both the single view and list queries
      queryClient.invalidateQueries({
        queryKey: ["saved-view", updatedView.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["saved-views", updatedView.page_type],
      });
    },
    onError: () => {
      // Handle error silently - UI should show appropriate message
    },
  });
}

// Hook to delete a saved view
export function useDeleteSavedView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      viewId,
      pageType,
    }: {
      viewId: string;
      pageType: string;
    }) => {
      await api.delete(`/saved-views/${viewId}/`);
      return { viewId, pageType };
    },
    onSuccess: (data) => {
      // Invalidate the list query for this page type
      queryClient.invalidateQueries({
        queryKey: ["saved-views", data.pageType],
      });
      // Also invalidate the single view query
      queryClient.invalidateQueries({ queryKey: ["saved-view", data.viewId] });
    },
    onError: () => {
      // Handle error silently - UI should show appropriate message
    },
  });
}
